import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileConvert } from '../../hooks/useFileConvert';
import Layout from '../../components/feature/Layout';
import ConversionSuccess from '../../components/feature/ConversionSuccess';

type ToolMode = 'merge' | 'split' | 'compress';

export default function PdfToolsPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ToolMode>('merge');
  const [isDragging, setIsDragging] = useState(false);
  const [pageRange, setPageRange] = useState('');
  const [originalSize, setOriginalSize] = useState<number>(0);

  const {
    file, files, status, progress, error, downloadUrl, fileName, compressedSize,
    setFile, setFiles, startConversion, startMultipleConversion, download, reset
  } = useFileConvert();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    // Set page title
    document.title = 'PDF Tools - Merge, Split & Compress PDF | FreePDFTools';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Free online PDF tools to merge multiple PDFs, split PDF pages, and compress PDF file size. Easy, secure, and no registration required.');
    
    // Set canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/pdf-tools`);
    
    // SoftwareApplication Schema
    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PDF Tools - Merge, Split, Compress",
      "url": `${siteUrl}/pdf-tools`,
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "Free online PDF tools to merge multiple PDFs, split PDF pages, and compress PDF file size. Easy and secure.",
      "featureList": [
        "Merge multiple PDF files into one",
        "Split PDF by page ranges",
        "Compress PDF to reduce file size",
        "Reorder PDF pages before merging",
        "No file size limits"
      ],
      "screenshot": `${siteUrl}/og-image.png`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "13950",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // HowTo Schema
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Use PDF Tools - Merge, Split, Compress",
      "description": "Step-by-step guide to merge, split, or compress PDF files online",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Select Tool Mode",
          "text": "Choose the tool you need: Merge PDFs, Split PDF, or Compress PDF",
          "url": `${siteUrl}/pdf-tools#step1`
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Upload PDF Files",
          "text": "Upload your PDF file(s). For merge, you can upload multiple files and reorder them",
          "url": `${siteUrl}/pdf-tools#step2`
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Process and Download",
          "text": "Click the process button and download your merged, split, or compressed PDF file",
          "url": `${siteUrl}/pdf-tools#step3`
        }
      ],
      "totalTime": "PT3M"
    };

    const softwareScript = document.createElement('script');
    softwareScript.type = 'application/ld+json';
    softwareScript.text = JSON.stringify(softwareSchema);
    document.head.appendChild(softwareScript);

    const howToScript = document.createElement('script');
    howToScript.type = 'application/ld+json';
    howToScript.text = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    return () => {
      document.head.removeChild(softwareScript);
      document.head.removeChild(howToScript);
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mode === 'merge') {
      if (e.target.files) {
        const newFiles = Array.from(e.target.files).slice(0, 10);
        setFiles([...files, ...newFiles].slice(0, 10));
      }
    } else if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setOriginalSize(selectedFile.size);
      if (mode === 'compress') startConversion('compress');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (mode === 'merge') {
      if (e.dataTransfer.files) {
        const newFiles = Array.from(e.dataTransfer.files).slice(0, 10);
        setFiles([...files, ...newFiles].slice(0, 10));
      }
    } else if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const selectedFile = e.dataTransfer.files[0];
      setFile(selectedFile);
      setOriginalSize(selectedFile.size);
      if (mode === 'compress') startConversion('compress');
    }
  };

  const handleModeChange = (newMode: ToolMode) => {
    setMode(newMode);
    reset();
    setPageRange('');
    setOriginalSize(0);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isProcessing = status === 'uploading' || status === 'converting' || status === 'downloading';
  const isCompleted = status === 'completed';
  const hasError = status === 'error';

  const dropZoneClass = (color: string) =>
    `border-2 border-dashed rounded-2xl p-14 text-center transition-all cursor-pointer ${
      isDragging ? `border-${color}-500 bg-${color}-50` : `border-gray-300 bg-gray-50 hover:border-${color}-400 hover:bg-${color}-50/40`
    }`;

  return (
    <Layout>
      <div className="bg-white min-h-screen">
        <div className="w-full bg-gray-50 border-b border-dashed border-gray-300 py-3">
          <div className="max-w-4xl mx-auto text-center text-gray-400 text-sm">{t('ads.topBanner')} 728×90</div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-tools-line text-3xl text-indigo-600"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('pdfToolsPage.title')}</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('pdfToolsPage.description')}</p>
          </div>

          {/* Mode Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-xl p-1">
              {(['merge', 'split', 'compress'] as ToolMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => handleModeChange(m)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${mode === m ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  <i className={`${m === 'merge' ? 'ri-merge-cells-horizontal' : m === 'split' ? 'ri-scissors-cut-line' : 'ri-file-reduce-line'} mr-2`}></i>
                  {t(`pdfTools.${m}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Merge Mode */}
          {mode === 'merge' && !isProcessing && !isCompleted && (
            <div className="mb-10">
              <div
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all mb-6 cursor-pointer ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/40'}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                  <i className="ri-file-add-line text-4xl text-indigo-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('pdfTools.addFiles')}</h3>
                <p className="text-gray-500 mb-6">{t('upload.or')}</p>
                <label className="inline-block cursor-pointer">
                  <input type="file" accept=".pdf" multiple onChange={handleFileSelect} className="hidden" />
                  <span className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors inline-block whitespace-nowrap">{t('upload.selectFile')}</span>
                </label>
                <p className="text-sm text-gray-400 mt-5">{t('pdfTools.maxFiles')} (2-10 {t('pdfTools.files')})</p>
              </div>

              {files.length > 0 && (
                <div className="space-y-3 mb-6">
                  {files.map((f, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-pdf-line text-xl text-red-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{f.name}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(f.size)}</p>
                      </div>
                      <div className="flex gap-2">
                        {index > 0 && (
                          <button onClick={() => { const nf = [...files]; [nf[index], nf[index-1]] = [nf[index-1], nf[index]]; setFiles(nf); }} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-arrow-up-line"></i>
                          </button>
                        )}
                        {index < files.length - 1 && (
                          <button onClick={() => { const nf = [...files]; [nf[index], nf[index+1]] = [nf[index+1], nf[index]]; setFiles(nf); }} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-arrow-down-line"></i>
                          </button>
                        )}
                        <button onClick={() => setFiles(files.filter((_, i) => i !== index))} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {files.length >= 2 && (
                <div className="text-center">
                  <button onClick={() => startMultipleConversion('merge')} className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-merge-cells-horizontal mr-2"></i>{t('pdfTools.mergeNow')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Split Mode */}
          {mode === 'split' && !isProcessing && !isCompleted && (
            <div className="mb-10">
              {!file ? (
                <div
                  className={`border-2 border-dashed rounded-2xl p-14 text-center transition-all cursor-pointer ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/40'}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                    <i className="ri-upload-cloud-2-line text-4xl text-indigo-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.dragDrop')}</h3>
                  <p className="text-gray-500 mb-6">{t('upload.or')}</p>
                  <label className="inline-block cursor-pointer">
                    <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
                    <span className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors inline-block whitespace-nowrap">{t('upload.selectFile')}</span>
                  </label>
                  <p className="text-sm text-gray-400 mt-5">{t('upload.fileSize')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-pdf-line text-2xl text-red-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                    <button onClick={reset} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdfTools.pageRange')}</label>
                    <input
                      type="text"
                      value={pageRange}
                      onChange={(e) => setPageRange(e.target.value)}
                      placeholder="1-3,5,7-9"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                    <p className="text-sm text-gray-500 mt-2">{t('pdfTools.pageRangeExample')}</p>
                  </div>
                  <div className="text-center">
                    <button
                      onClick={() => pageRange.trim() && startConversion('split')}
                      disabled={!pageRange.trim()}
                      className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-scissors-cut-line mr-2"></i>{t('pdfTools.splitNow')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compress Mode */}
          {mode === 'compress' && !file && !isProcessing && !isCompleted && (
            <div className="mb-10">
              <div
                className={`border-2 border-dashed rounded-2xl p-14 text-center transition-all cursor-pointer ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/40'}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                  <i className="ri-upload-cloud-2-line text-4xl text-indigo-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.dragDrop')}</h3>
                <p className="text-gray-500 mb-6">{t('upload.or')}</p>
                <label className="inline-block cursor-pointer">
                  <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
                  <span className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors inline-block whitespace-nowrap">{t('upload.selectFile')}</span>
                </label>
                <p className="text-sm text-gray-400 mt-5">{t('upload.fileSize')}</p>
              </div>
            </div>
          )}

          {/* Processing */}
          {isProcessing && (
            <div className="border-2 border-indigo-200 rounded-2xl p-12 text-center bg-indigo-50 mb-10">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                <i className="ri-loader-4-line text-4xl text-indigo-600 animate-spin"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {status === 'uploading' && t('upload.uploading')}
                {status === 'converting' && t('upload.converting')}
                {status === 'downloading' && t('upload.preparing')}
              </h3>
              <div className="max-w-md mx-auto">
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div className="bg-indigo-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                </div>
                <p className="text-sm text-gray-600">{progress}%</p>
              </div>
            </div>
          )}

          {/* Completed */}
          {isCompleted && (
            <div className="mb-10">
              <ConversionSuccess
                fileName={fileName}
                originalName={mode === 'merge' ? `${files.length} 个文件合并` : file?.name}
                accentColor="indigo"
                extraInfo={
                  mode === 'compress' && originalSize > 0 ? (
                    <div className="flex justify-center gap-8 text-sm">
                      <div>
                        <p className="text-gray-500">{t('pdfTools.originalSize')}</p>
                        <p className="font-semibold text-gray-900">{formatFileSize(originalSize)}</p>
                      </div>
                      <div className="w-px bg-gray-300"></div>
                      <div>
                        <p className="text-gray-500">{t('pdfTools.compressedSize')}</p>
                        <p className="font-semibold text-green-600">
                          {compressedSize ? formatFileSize(compressedSize) : formatFileSize(Math.floor(originalSize * 0.6))}
                        </p>
                      </div>
                    </div>
                  ) : undefined
                }
                onDownload={download}
                onReset={reset}
              />
            </div>
          )}

          {/* Error */}
          {hasError && error && (
            <div className="border-2 border-red-200 rounded-2xl p-12 text-center bg-red-50 mb-10">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                <i className="ri-error-warning-line text-4xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.error')}</h3>
              <p className="text-gray-600 mb-6">{error.message}</p>
              <button onClick={reset} className="px-8 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-refresh-line mr-2"></i>{t('upload.retry')}
              </button>
            </div>
          )}

          <div className="w-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl py-8 mb-10 text-center text-gray-400 text-sm">
            {t('ads.middleBanner')} 728×90
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: 'ri-merge-cells-horizontal', key: 'feature1' },
              { icon: 'ri-infinity-line', key: 'feature2' },
              { icon: 'ri-gift-line', key: 'feature3' },
            ].map((f) => (
              <div key={f.key} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${f.icon} text-xl text-indigo-600`}></i>
                </div>
                <h3 className="font-semibold text-gray-900">{t(`pdfToolsPage.${f.key}`)}</h3>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t('upload.howItWorks')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="text-center">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">{n}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t(`upload.step${n}`)}</h3>
                  <p className="text-sm text-gray-600">{t(`upload.step${n}Desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
